import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { openSettingsTab } from "../../app/settingsPanel.js";
import { useMerchRollup, type MerchBestSeller } from "../../app/hooks/useMerchRollup.js";
import styles from "./MerchRollupCard.module.scss";

const DEGRADED = "—";

/**
 * Whole-dollar USD (no cents) — the shot-08 merch card figures.
 *
 * NULL IS NOT ZERO, and this used to coerce `cents ?? 0` (audit 12.10). A figure we do not hold printed as
 * `$0`, which reads as "your store sold nothing" rather than "we cannot see this" — and the reference names
 * the exact case it broke: on a CONNECTED store with real zero sales its specimen shows Today `$0` /
 * 30-day `$0` / **Lifetime `-`**, because lifetime needs a `read_all_orders` scope the token may not carry.
 * A scope we do not hold is not revenue of zero. The sibling `MerchSpotlight` always got this right, so the
 * two merch surfaces disagreed on the same number.
 *
 * A REAL `0` still renders `$0` — that is a measurement, and the connected-zero pill state depends on it.
 */
function usd(cents: number | null | undefined): string {
  if (cents == null) return DEGRADED;
  return (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * The dashboard Merch rollup widget — today / 30-day / lifetime sales, the pending-to-fulfill count, and
 * best-sellers from the merchant's Redis vendor cache. "Open" deep-links to the store admin (server-provided
 * adminUrl) — READ-ONLY, no in-app fulfillment.
 *
 * THE CARD KEEPS ITS SHAPE WHEN NOTHING IS CONNECTED (audit 12.11). It used to swap the whole body for an
 * `EmptyState`; both reference specimens instead keep the three totals reading `-` and turn the Pending row
 * into the CONNECT affordance. That matters beyond parity: a card that changes shape when empty reads as one
 * that failed to load, and the swap also threw away the only place the card says what to do about it. What
 * differs between the two states is the header badge and whether the Pending row is a control:
 *
 *   connected     · dot + "Connected"  · Pending is STATIC   → "N to fulfill"
 *   not connected · "Not connected"    · Pending is a BUTTON → "-" + "Connect →" (opens Settings › Connections)
 *
 * The badge used to render today's SALES FIGURE, which is a different fact from whether a store is attached —
 * and an em-dash there was indistinguishable from a connected store that had sold nothing today.
 *
 * ITS FIGURES ARE THE ONLINE LANE ONLY (ART-138, 2026-09-10) — the exact complement of what each show's
 * settlement takes, so a register sale is no longer reported here AND on the show it was rung up at. When the
 * vendor names its channels and there is in-person money to name, the split line discloses it; see the
 * comment at that line for why a zero and an unknown both draw nothing.
 *
 * IT IS THE ONE ACCENT-TINTED CARD IN THE RAIL. The ground and the inset white stat chips are a single
 * treatment (see the stylesheet's opening note) — this card is not the place to reach for one without the
 * other.
 *
 * VISIBILITY IS `money:merch`, read as a CAPABILITY rather than re-derived from a role. The card is sales
 * money end to end, so the route took the money token on 2026-09-03. This used to hard-code
 * `effectiveRole === "content"` — a client-side restatement of a server rule, and the shape that drifts: it
 * named the one role excluded at the time, so every role added since defaulted to VISIBLE. Reading the token
 * means the two cannot disagree, and the seats that now lose the card — a plain band member, the production
 * crew, every intern and support seat — need no enumeration here.
 */
export function MerchRollupCard() {
  // Skip the read entirely without the token: the route would 403, and a card that renders its loading
  // state before vanishing is worse than one that never mounts.
  const canSeeMerchMoney = useCapability("money:merch");
  const q = useMerchRollup({ enabled: canSeeMerchMoney });
  if (!canSeeMerchMoney) return null;

  const card = q.data;
  const connected = card?.connected === true;
  const rollup = card?.rollup ?? null;
  const bestSellers: MerchBestSeller[] = rollup?.bestSellers ?? [];
  const adminUrl = card?.adminUrl ?? null;

  return (
    <section className={styles.box} aria-labelledby="merch-heading">
      <header className={styles.head}>
        <h3 id="merch-heading" className={styles.title}>
          Merch
        </h3>
        {card && (
          <span className={styles.pill} data-status={connected ? "connected" : undefined}>
            {/* The dot is the reference's `live-pill__dot`, and only the live variant carries one. */}
            {connected && <span className={styles.pillDot} aria-hidden="true" />}
            {connected ? "Connected" : "Not connected"}
          </span>
        )}
      </header>

      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner />
        </div>
      ) : q.isError ? (
        <EmptyState
          title="Couldn't load merch"
          description={q.error instanceof ApiError ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          {/* Always rendered, connected or not — a disconnected store degrades to three em-dashes rather than
              taking the card's shape away. `usd(null)` IS the em-dash, so no branch is needed here. */}
          <div className={styles.figures}>
            <Figure label="Today" value={usd(connected ? rollup?.salesTodayCents : null)} />
            <Figure label="30-day" value={usd(connected ? rollup?.sales30dCents : null)} />
            <Figure label="Lifetime" value={usd(connected ? rollup?.salesLifetimeCents : null)} />
          </div>

          {/* ART-138. The figures above are the ONLINE lane — the exact complement of what each show's
              settlement takes — so a store that rings sales up at a merch table now reads LOWER here than it
              did, by exactly the amount attributed to its shows. This line is what makes that legible rather
              than merely smaller.

              Two absences, drawn the same way for different reasons. NULL is "this vendor names no channel"
              (Square, whose orders are all Unknown): there is no split, and claiming one would invent it.
              ZERO is a measurement and still draws nothing, because "· POS $0" on a web-only store is a
              permanent line about a lane the band does not have. The server keeps the two apart (see
              `MerchRollup.PosSales30dCents`) — what matches is only what we would SAY, and in both cases the
              honest thing to say is nothing. */}
          {connected && (rollup?.posSales30dCents ?? 0) > 0 && (
            <p className={styles.split}>
              Online {usd(rollup?.sales30dCents)} · POS {usd(rollup?.posSales30dCents)}
            </p>
          )}

          {connected ? (
            <p className={styles.pending}>
              <span className={styles.pendingLabel}>Pending</span>
              <span className={styles.pendingCount}>{rollup?.pendingCount ?? 0} to fulfill</span>
            </p>
          ) : (
            // A BUTTON, not an anchor: Settings is an in-app overlay (`settingsPanel.ts`), so there is no URL
            // to href — and the reference's `<a href="#">` is a specimen artefact, not a destination.
            <button type="button" className={styles.connect} onClick={() => openSettingsTab("connections")}>
              <span className={styles.pendingLabel}>Pending</span>
              <span className={styles.pendingCount}>{DEGRADED}</span>
              <span className={styles.connectArrow}>Connect →</span>
            </button>
          )}

          {connected && bestSellers.length > 0 && (
            <div className={styles.sellers}>
              {/* The window is named because the ranking HAS one: it is composed over 30 days server-side,
                  where it used to run over the source's whole 365-day pull under this bare heading. */}
              <span className={styles.sellersLabel}>Best sellers · 30d</span>
              <ul className={styles.sellerList}>
                {bestSellers.map((s, i) => (
                  <li key={`${s.title}-${i}`} className={styles.seller}>
                    <span className={styles.sellerTitle}>{s.title || "Untitled"}</span>
                    <span className={styles.sellerUnits}>{s.units ?? 0} sold</span>
                    <span className={styles.sellerGross}>{usd(s.grossCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {connected && adminUrl && (
            <div className={styles.actions}>
              <a className={styles.open} href={adminUrl} target="_blank" rel="noreferrer">
                Open
              </a>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * One inset stat chip. The LABEL IS FIRST, which is the reference's order and was inverted here: the three
 * chips are read as a row, so a reader looking for "30-day" finds it only if the labels share a line. With
 * the value first, all three lead with a dollar figure and the labels are the thing you hunt for.
 */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.figure}>
      <span className={styles.figureLabel}>{label}</span>
      <span className={styles.figureValue}>{value}</span>
    </div>
  );
}
